import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../client'
import type { components } from '../generated/schema'

export type BillingStatus = components['schemas']['BillingStatusOut']
type CheckoutSession = components['schemas']['CheckoutSessionOut']

export const billingKey = ['billing-status'] as const

/**
 * Read before an upload, so someone out of credits is told before choosing a
 * file rather than after (audit section 7.1: the old app checked nothing and
 * surfaced a 402 as a generic red toast).
 *
 * `can_run_analysis` is the backend's answer. The frontend does no credit
 * arithmetic of its own — entitlement is ledger-driven and server-owned.
 */
export function useBillingStatus() {
  return useQuery<BillingStatus>({
    queryKey: billingKey,
    queryFn: () => apiGet<BillingStatus>('/billing/status'),
    staleTime: 15_000,
  })
}

/**
 * Every purchase is a hosted Stripe page. The API returns a URL and the browser
 * goes there; no card form is ever rendered here. The mutation resolves to the
 * URL rather than navigating itself, so a caller can show its own error state
 * and the navigation is one visible line at the call site.
 */
export function useStartSubscription() {
  return useMutation({
    retry: false,
    mutationFn: () => apiPost<CheckoutSession>('/billing/checkout'),
  })
}

export function useBuyCredits() {
  return useMutation({
    retry: false,
    mutationFn: (quantity: number) => apiPost<CheckoutSession>('/billing/checkout/credits', { quantity }),
  })
}

export function useOpenBillingPortal() {
  return useMutation({
    retry: false,
    mutationFn: () => apiPost<CheckoutSession>('/billing/portal'),
  })
}

/** Stripe has just sent the user back. What they paid for lands by webhook. */
export function useRefreshBilling() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: billingKey })
}
